// domain/constants/coverage.js

/**
 * What the policyholder is TO the client, in the direction a user actually thinks.
 *
 * Asked that way round rather than the reverse, because nobody at intake thinks "the client is the
 * child". The payer-facing code runs the other way and is derived at claim assembly, never typed
 * and never stored.
 *
 * PARENT and LEGAL_GUARDIAN are separate values even though both are a caregiver holding the
 * policy, because they resolve to different payer-facing codes. LEGAL_GUARDIAN and OTHER resolve to
 * the same one, and that is fine: two user-facing values sharing a code loses nothing, whereas
 * collapsing them would discard a distinction the person entering it can actually make.
 *
 * THIS IS THE VOCABULARY OF ONE ATTRIBUTE, not the concept. The concept is `Policyholder`, which
 * pairs a relationship with the person who holds the policy; the rules about that pair live there.
 * Same split as VERIFICATION_SOURCE below and `EligibilityVerification`.
 *
 * @domainObject value-object
 * @invariant Extension stays additive. A new value must never require reshaping coverages already
 *   stored under an existing one.
 * @invariant A sufficient INPUT to the payer-facing derivation, never a key into a lookup table.
 *   Whether the client is transmitted as the policyholder or as a dependent also depends on
 *   whether they hold their own member id with that payer, which is a transport decision rather
 *   than a fact about who holds the policy. Do not add a stored code alongside this.
 * @typedef {'self' | 'parent' | 'legal_guardian' | 'spouse' | 'other'} PolicyholderRelationship
 */
export const POLICYHOLDER_RELATIONSHIP = {
  SELF: 'self',
  PARENT: 'parent',
  LEGAL_GUARDIAN: 'legal_guardian',
  SPOUSE: 'spouse',
  OTHER: 'other',
};

/** The vocabulary as a list, so validation can ask whether a raw value is a member of it. */
export const POLICYHOLDER_RELATIONSHIP_VALUES = Object.values(POLICYHOLDER_RELATIONSHIP);

/*
 * A coverage's state is not one enum. Four things vary independently, and only two of them are
 * facts about the coverage at all:
 *
 *   effective period       stored dates → COVERAGE_STATUS, derived
 *   eligibilityVerified    stored boolean — "we confirmed with the payer that this is active"
 *   billing paused         NOT stored here — a BillingIntent aggregate pointing at the coverage
 *   readiness              NOT stored anywhere — derived from the policyholder's details and the
 *                          member id, see `policyholderReadiness`
 *
 * Collapsing the first two into a single status would force states that genuinely co-occur to
 * overwrite each other. The third is a different kind of thing entirely: a decision about what we
 * intend to do, not a fact about the payer relationship, so it does not live on this aggregate.
 *
 * The fourth is a different kind again, and the newest. Eligibility is the PAYER'S answer about the
 * plan; readiness is about OUR OWN record of who holds it. A coverage can be in force, verified,
 * unpaused and still unready because nobody has the parent's date of birth. It is also the only one
 * that spans two records, since the member id lives on the coverage and the rest on a person.
 *
 * Nothing anywhere stores "active". Whether a coverage is billed is derived from all of them — see
 * `coverageBilling.isBillableNow` for the first three, `policyholderReadiness` for the fourth.
 */

/**
 * Derived lifecycle relative to a date. NEVER stored — a persisted status is wrong the day after
 * it is written.
 *
 * @domainObject value-object
 * @invariant Always derived as-of an explicit date, never persisted and never read from a clock
 *   inside a rule. A stored status rots the moment the effective period rolls past it.
 * @invariant Says nothing about whether the coverage is BILLED. That needs the two stored
 *   decisions as well — see `coverageBilling.isBillableNow`.
 * @typedef {'upcoming' | 'active' | 'ended'} CoverageStatus
 */
export const COVERAGE_STATUS = {
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  ENDED: 'ended',
};

/**
 * Where a verification verdict came from.
 *
 * Only manual today. The value exists so that an automated check — Stedi 270/271 — is a new member
 * of this vocabulary rather than a migration of every stored coverage, and so that manual entry
 * can become an override ALONGSIDE automation rather than being the only thing that ever wrote it.
 *
 * @domainObject value-object
 * @invariant A verdict always carries its source. Once more than one exists, an unattributed
 *   verified/not-verified cannot be reasoned about — a stale automated check and a deliberate
 *   manual override are different facts that would otherwise look identical.
 * @typedef {'manual'} VerificationSource
 */
export const VERIFICATION_SOURCE = {
  MANUAL: 'manual',
};

/** Lowest position is billed first. Coordination of benefits starts at 1 (primary). */
export const PRIMARY_COB_RANK = 1;
